<template>
  <div>
    <div class="structured-top">
      <h1 v-mark-word>{{ $t('myConsentRequests.title') }}</h1>
      <span class="lead">{{ $t('myConsentRequests.hint') }}</span>
    </div>
    <div>
      <BTabs>
        <BTab v-for="consentRequests in [activeConsentRequests, archivedConsentRequests]" :title="consentRequests.title" :key="consentRequests.title" class="position-relative">
          <div v-if="!consentRequests.data.length">
            <table class="table structure-table">
              <thead>
              <tr class="consent-table-row">
                <td class="d-none d-xl-table-cell col-12">
                  <span v-if="consentRequests === activeConsentRequests">{{ $t('myConsentRequests.emptyListActive')}}</span>
                  <span v-else>{{ $t('myConsentRequests.emptyListArchived')}}</span>
                </td>
              </tr>
              </thead>
            </table>
          </div>
          <div v-else>
            <table class="table structure-table">
              <thead>
                <tr class="consent-table-row">
                  <th class="d-none d-xl-table-cell col-2">{{ $t('myConsents.table.columnStatus') }}</th>
                  <th class="d-none d-xl-table-cell col-2">{{ $t('myConsents.table.columnDate') }}</th>
                  <th class="d-none d-xl-table-cell col-2">{{ $t('myConsents.table.columnProduct') }}</th>
                  <th class="d-none d-xl-table-cell col-2">{{ $t('myConsentRequests.table.columnSubject') }}</th>
                  <th class="d-none d-xl-table-cell col-4" />
                </tr>
              </thead>
              <tbody>
                <template v-for="consentRequest in consentRequests.data" :key="consentRequest.id">
                  <tr class="consent-table-row">
                    <td class="consent-row-label" :class=labelStyle(consentRequest.status)>{{ statusDescription(consentRequest.status) }}</td>
                    <td >{{ consentRequest.createdAt }}</td>
                    <td class="fw-bold">{{ productDescription(consentRequest.type) }}</td>
                    <td>
                      <div class="gray-600 fw-bold">{{ consentRequest.subjectCode }}</div>
                      <div>{{ consentRequest.subjectName }}</div>
                    </td>
                    <td class="text-end">
                      <div v-for="log in consentRequest.log" class="mb-1">
                        <span class="gray-600 fw-bold">{{ log.value }}</span>
                        <span v-if="log.time.length">: {{ log.time }}</span>
                      </div>
                      <div class="btn-col" v-if="consentRequest.actions.canRequestAgain || consentRequest.actions.canBuy">
                        <a v-if="consentRequest.actions.canRequestAgain" class="btn btn-success m-1" @click="actionRequestAgain(consentRequest.type)">{{ $t('myConsentRequests.button.askAgain') }}</a>
                        <a v-if="consentRequest.actions.canBuy" class="btn btn-success m-1" @click="actionBuy(consentRequest)">{{ $t('myConsentRequests.button.buy') }}</a>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </BTab>
      </BTabs>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import { BTab, BTabs } from 'bootstrap-vue-next';

export default {
  components: { BTab, BTabs },
  data: () => ({
    activeConsentRequests: {
        title: '',
        data: []
    },
    archivedConsentRequests: {
      title: '',
      data: []
    }
  }),
  computed: {
    ...mapState('session', ['profile']),
  },
  watch: {
    '$i18n.locale': function () {
      this.loadTabNames();
      this.loadConsentRequests();
    },
  },
  methods: {
    ...mapActions('purchase', ['addToCart']),
    ...mapActions('consentRequests', ['getMyConsentRequests', 'recreateConsent', 'buyConsent']),
    ...mapMutations('session', ['redirectAfterLogin']),
    redirectIfNotLoggedIn() {
      if (!this.profile.personId) {
        this.redirectAfterLogin(this.$route.fullPath);
        this.$router.push({ name: `login-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } });
      }
    },
    loadConsentRequests() {
      this.getMyConsentRequests()
        .then((response) => {
          this.activeConsentRequests.data = response.activeData;
          this.archivedConsentRequests.data = response.archivedData;
        });
    },
    loadTabNames() {
      this.activeConsentRequests.title = this.$t('myConsents.activeTab');
      this.archivedConsentRequests.title = this.$t('myConsents.historyTab');
    },
    statusDescription(status) {
      if (status === 'CREATED') {
        return this.$t('myConsents.status.created');
      } else if (status === 'APPROVED') {
        return this.$t('myConsents.status.approved');
      } else if (status === 'DENIED') {
        return this.$t('myConsents.status.denied');
      } else if (status === 'EXPIRED') {
        return this.$t('myConsents.status.expired');
      } else if (status === 'RESCINDED') {
        return this.$t('myConsents.status.denied');
      } else if (status === 'USED') {
        return this.$t('myConsents.status.approved');
      } else {
        return this.$t('myConsents.status.unknown');
      }
    },
    labelStyle(status) {
      return 'label-consent-' + status.toLowerCase();
    },
    productDescription(product) {
      if (product === 'FACTS AND FIGURES') {
        return this.$t('myConsents.product.factsAndFigures');
      } else if (product === 'PAYMENT DEFAULTS') {
        return this.$t('myConsents.product.paymentDefaults');
      } else if (product === 'RELATED COMPANIES') {
        return this.$t('myConsents.product.relatedCompanies');
      } else {
        return this.$t('myConsents.product.unknown');
      }
    },
    productCode(productType) {
      if (productType === 'FACTS AND FIGURES') {
        return 'fact-person';
      }
      if (productType === 'PAYMENT DEFAULTS') {
        return 'payment-defaults';
      }
      if (productType === 'RELATED COMPANIES') {
        return 'companies-on-person';
      }
    },
    actionRequestAgain(productType) {
      this.$router.push('/products/' + this.url(productType));
    },
    actionBuy(consent) {
      let item = {
        details: {
          personId: consent.subjectCode,
          name: consent.subjectName,
        },
      }
      return this.addToCart({ ...item, id: this.productCode(consent.type) })
          .then(() => this.$router.push({ name: `purchase-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }));
    },
    url(productType) {
      if (productType === 'FACTS AND FIGURES') {
        return 'facts-and-figures-on-a-private-person';
      }
      if (productType === 'PAYMENT DEFAULTS') {
        return 'payment-defaults-on-private-person';
      }
      if (productType === 'RELATED COMPANIES') {
        return 'companies-of-a-private-person';
      }
    },
  },
  created() {
    this.redirectIfNotLoggedIn();
    this.loadTabNames();
    this.loadConsentRequests();
  },
  metaInfo() {
    return {
      title: this.$t('myConsents.meta.title'),
      meta: [],
    };
  },
};
</script>

<style scoped>
</style>
